import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import EventIcon from "@mui/icons-material/Event";
import EventRepeat from "@mui/icons-material/EventRepeat";
import FactoryOutlinedIcon from "@mui/icons-material/FactoryOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import PersonFilledIcon from "@mui/icons-material/Person";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import { Button, Stack, Tooltip, useTheme } from "@mui/material";
import { cyan, purple } from "@mui/material/colors";
import { SaveButton } from "components/SaveButton";
import { getUtcDisplayDate } from "encamp-shared/src/utils/date";
import { getFullName } from "encamp-shared/src/utils/name";
import { Frequency } from "generated-graphql/graphql";
import pluralize from "pluralize";
import { useCallback, useEffect, useState } from "react";
import { UseFormReturn } from "react-hook-form";

import { AddDocumentControl } from "./DocumentsControl";
import { DueDatePopover } from "./DueDatePopover";
import { FacilityPickerPopover } from "./FacilityPickerPopover";
import { ReminderDialog } from "./ReminderDaysFormControl";
import { getOverdueTooltip, isTaskOverdue } from "./statusStyles";
import { TagsControl } from "./TagsControl";
import { TaskFormButton } from "./TaskFormButton";
import { TaskFormType } from "./types";
import { UserPickerPopover } from "./UserPickerPopover";
import { getFrequencyLabel } from "./util.labels";

export interface TaskPickerToolbarProps {
  form: UseFormReturn<TaskFormType>;

  onCancel?: () => void;
  onSubmit?: () => void;
  isSubmitting?: boolean;

  /** Whether to show the action buttons (cancel and submit) */
  showActionButtons?: boolean;
  /** Text for the cancel button */
  cancelButtonText?: string;
  /** Whether the cancel button is disabled */
  cancelButtonDisabled?: boolean;
  submitButtonText?: string;
  submitButtonHelperText?: (isDirty: boolean) => string;
  /** If true, submit button is always enabled regardless of form dirty state */
  submitAlwaysEnabled?: boolean;
}

export const TaskPickerToolbar: React.FC<TaskPickerToolbarProps> = ({
  form,
  showActionButtons,
  cancelButtonText,
  onCancel,
  onSubmit,
  isSubmitting,
  cancelButtonDisabled,
  submitButtonText,
  submitButtonHelperText,
  submitAlwaysEnabled,
}) => {
  const theme = useTheme();
  const { control, watch } = form;

  const deadline = watch("deadline");
  const frequency = watch("frequency");
  const interval = watch("interval");
  const taskStatus = watch("taskStatus");
  const selectedAssignee = watch("assignee");
  const selectedWatchers = watch("watchers");
  const selectedFacilityIds = watch("facilityIds");
  const reminderDays = watch("reminderDays");

  const overdue = isTaskOverdue({ deadline, taskStatus });

  // When the frequency is removed, reset the interval to 1 if it is < 1
  useEffect(() => {
    if (frequency === Frequency.Once && (!interval || interval < 1)) {
      form.setValue("interval", 1);
    }
  }, [frequency, interval, form]);

  const [assigneePopoverAnchorEl, setAssigneePopoverAnchorEl] = useState<HTMLElement | null>(null);
  const [watcherPopoverAnchorEl, setWatcherPopoverAnchorEl] = useState<HTMLElement | null>(null);
  const [facilityPopoverAnchorEl, setFacilityPopoverAnchorEl] = useState<HTMLElement | null>(null);
  const [dueDatePopoverAnchorEl, setDueDatePopoverAnchorEl] = useState<HTMLElement | null>(null);

  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);

  const handleCancel = useCallback(() => onCancel?.(), [onCancel]);

  const getDeadlineIcon = useCallback(() => {
    if (!deadline) {
      return <EventIcon />;
    }
    if (frequency === Frequency.Once) {
      return <EventIcon color="primary" />;
    }
    return <EventRepeat color="info" />;
  }, [deadline, frequency]);

  const getDeadlineLabel = useCallback(() => {
    if (!deadline) {
      return "Set due date";
    }

    // Non-recurring: show the date as-is
    if (frequency === Frequency.Once) {
      return getUtcDisplayDate(deadline, "full");
    }

    const abbreviatedDate = getUtcDisplayDate(deadline, "med");

    // Recurring: show clean recurrence label
    const normalizedInterval = interval && interval > 0 ? interval : 1;
    const unit = getFrequencyLabel(frequency);

    if (normalizedInterval === 1) {
      return `${abbreviatedDate}, every ${unit}`;
    }

    return `${abbreviatedDate}, every ${normalizedInterval} ${pluralize(unit, normalizedInterval)}`;
  }, [deadline, frequency, interval]);

  const reminderLabel = useCallback(() => {
    if (reminderDays.length === 0) {
      return "Set reminders";
    } else if (reminderDays.length === 1) {
      const days = reminderDays[0].days;
      return `${days} ${pluralize("day", days)} prior`;
    }
    return `${reminderDays.length} reminders`;
  }, [reminderDays]);

  const hasSelectedFacilities = selectedFacilityIds.length > 0;
  let facilityLabel = "Facilities";
  let facilityButtonMinWidth = "110px";
  if (hasSelectedFacilities) {
    facilityLabel = `${selectedFacilityIds.length} ${pluralize(
      "facility",
      selectedFacilityIds.length
    )}`;
    facilityButtonMinWidth = "110px";
  }

  const hasSelectedWatchers = selectedWatchers.length > 0;
  let watcherLabel = "Watchers";
  if (hasSelectedWatchers) {
    watcherLabel = `${selectedWatchers.length} ${pluralize("watcher", selectedWatchers.length)}`;
  }

  const watcherButtonMinWidth = "100px";

  return (
    <>
      <TaskFormButton
        control={control}
        name="assignee"
        icon={
          selectedAssignee ? (
            <PersonFilledIcon sx={{ color: cyan[200] }} />
          ) : (
            <PersonAddOutlinedIcon />
          )
        }
        label={
          selectedAssignee
            ? getFullName(selectedAssignee.person) || selectedAssignee.email
            : "Assignee"
        }
        onClick={(e) => setAssigneePopoverAnchorEl(e.currentTarget)}
        sx={{ minWidth: "108px" }}
        tooltipTitle="Choose assignee"
      />
      <UserPickerPopover
        formFieldName="assignee"
        open={Boolean(assigneePopoverAnchorEl)}
        selectedIds={selectedAssignee ? [selectedAssignee.id] : []}
        form={form}
        onClose={() => setAssigneePopoverAnchorEl(null)}
        anchorEl={assigneePopoverAnchorEl}
      />

      <TaskFormButton
        control={control}
        name="deadline"
        icon={getDeadlineIcon()}
        label={
          overdue && deadline ? (
            <Stack
              component="span"
              direction="row"
              alignItems="center"
              gap={0.5}
              sx={{ fontStyle: "italic" }}
            >
              <Tooltip title={getOverdueTooltip(deadline)} arrow>
                <ErrorOutlineIcon sx={{ fontSize: 16 }} color="error" />
              </Tooltip>
              {getDeadlineLabel()}
            </Stack>
          ) : (
            getDeadlineLabel()
          )
        }
        ariaLabel={getDeadlineLabel()}
        onClick={(e) => setDueDatePopoverAnchorEl(e.currentTarget)}
        sx={{
          minWidth: "140px",
          ...(overdue && {
            color: theme.palette.error.main,
            borderColor: theme.palette.error.main,
            "&:hover": {
              borderColor: theme.palette.error.dark,
              backgroundColor: `${theme.palette.error.main}08`,
            },
          }),
        }}
        tooltipTitle={
          overdue && deadline ? getOverdueTooltip(deadline) : "Set deadline and recurrence"
        }
      />
      <DueDatePopover
        open={Boolean(dueDatePopoverAnchorEl)}
        onClose={() => setDueDatePopoverAnchorEl(null)}
        anchorEl={dueDatePopoverAnchorEl}
        control={control}
      />
      <TaskFormButton
        control={control}
        name="reminderDays"
        icon={
          <NotificationsActiveOutlinedIcon
            color={reminderDays.length > 0 ? "secondary" : "inherit"}
          />
        }
        label={reminderLabel()}
        onClick={() => setReminderDialogOpen(true)}
        sx={{ minWidth: "120px" }}
        tooltipTitle="Configure reminders"
      />
      <ReminderDialog
        open={reminderDialogOpen}
        onClose={() => setReminderDialogOpen(false)}
        control={control}
      />
      <TaskFormButton
        control={control}
        name="facilityIds"
        icon={
          selectedFacilityIds.length > 0 ? (
            <FactoryOutlinedIcon sx={{ color: purple[400] }} />
          ) : (
            <FactoryOutlinedIcon />
          )
        }
        label={facilityLabel}
        onClick={(e) => setFacilityPopoverAnchorEl(e.currentTarget)}
        sx={{ minWidth: facilityButtonMinWidth }}
        tooltipTitle="Associate facilities"
      />
      <FacilityPickerPopover
        formFieldName="facilityIds"
        open={Boolean(facilityPopoverAnchorEl)}
        selectedIds={selectedFacilityIds}
        form={form}
        onClose={() => setFacilityPopoverAnchorEl(null)}
        anchorEl={facilityPopoverAnchorEl}
        allowMultiselect={true}
        mapValue={(facility) => facility.id}
      />
      <TaskFormButton
        control={control}
        name="watchers"
        icon={
          selectedWatchers.length > 0 ? (
            <PeopleOutlinedIcon color="success" />
          ) : (
            <PeopleOutlinedIcon />
          )
        }
        label={watcherLabel}
        onClick={(e) => setWatcherPopoverAnchorEl(e.currentTarget)}
        sx={{ minWidth: watcherButtonMinWidth }}
        tooltipTitle="Manage watchers"
      />
      <UserPickerPopover
        formFieldName="watchers"
        open={Boolean(watcherPopoverAnchorEl)}
        selectedIds={selectedWatchers.map((watcher) => watcher.id)}
        form={form}
        onClose={() => setWatcherPopoverAnchorEl(null)}
        anchorEl={watcherPopoverAnchorEl}
        allowMultiselect={true}
      />
      <TagsControl control={control} />
      <Stack
        direction="row"
        alignItems="center"
        gap={theme.spacing(1)}
        sx={{
          ml: { xs: 0, md: "auto" },
          width: { xs: "100%", md: "auto" },
          justifyContent: { xs: "flex-end", md: "flex-start" },
        }}
      >
        <AddDocumentControl
          control={control}
          tooltipTitle="Attach document"
          ariaLabel="Attach document"
        />
        {showActionButtons && (
          <>
            <Button onClick={handleCancel} disabled={isSubmitting || cancelButtonDisabled}>
              {cancelButtonText}
            </Button>
            {(() => {
              const title = watch("title");
              const missing: string[] = [];
              if (!title || !title.trim()) missing.push("title");
              if (!deadline) missing.push("due date");

              const dirtinessRequired = !submitAlwaysEnabled && !form.formState.isDirty;
              const isDisabled = dirtinessRequired || missing.length > 0;

              let tooltip: string | undefined;
              if (missing.length > 0) {
                const formatter = new Intl.ListFormat("en", {
                  style: "long",
                  type: "conjunction",
                });
                tooltip = `Set a ${formatter.format(missing)} to save this task.`;
              } else if (dirtinessRequired) {
                tooltip = submitButtonHelperText
                  ? submitButtonHelperText(form.formState.isDirty)
                  : "Make a change to enable saving.";
              } else if (submitButtonHelperText) {
                tooltip = submitButtonHelperText(form.formState.isDirty);
              }

              return (
                <Tooltip title={tooltip ?? ""} arrow disableHoverListener={!tooltip}>
                  <span>
                    <SaveButton
                      hideStartIcon
                      loading={isSubmitting}
                      saveText={submitButtonText}
                      disabled={isDisabled}
                      onClick={onSubmit}
                    />
                  </span>
                </Tooltip>
              );
            })()}
          </>
        )}
      </Stack>
    </>
  );
};
